import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from '../../i18n';
import { GameConfig } from '../../config/types';
import { config } from '../../config';
import { isMonthlyFreeGame } from '../../config/monthlyFreeGames';
import { GameCarouselTile } from './GameCarouselTile';

interface GameCarouselProps {
  games: GameConfig[];
  currentGameType?: string;
  votes?: Record<string, number>;
  onSelectGame: (gameType: string) => void;
  onPremiumClick?: () => void;
  isPremiumRoom?: boolean;
}

const getVisibleColumns = (): number => {
  const width = window.innerWidth;
  if (width <= 768) return 1;
  if (width <= 1024) return 2;
  if (width < 1440) return 3;
  return 4;
};

const CarouselChevron: React.FC<{ direction: 'left' | 'right' }> = ({ direction }) => (
  <svg
    className="carousel-arrow-icon"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="3"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    {direction === 'left' ? (
      <path d="M15 6l-6 6 6 6" />
    ) : (
      <path d="M9 6l6 6-6 6" />
    )}
  </svg>
);

const getSelectedAccent = (): { color: string; rgb: string } => {
  if (config.appId === 'blitz') {
    return { color: '#ef4444', rgb: '239, 68, 68' };
  }
  return {
    color: 'var(--color-primary)',
    rgb: 'var(--color-primary-rgb)',
  };
};

/** Center a tile in the scrollport (snap-friendly). Returns true if a scroll was started. */
const scrollTileIntoView = (tile: HTMLDivElement, container: HTMLDivElement): boolean => {
  const tileRect = tile.getBoundingClientRect();
  const containerRect = container.getBoundingClientRect();
  const tileLeft = tileRect.left - containerRect.left + container.scrollLeft;
  const target = tileLeft + tileRect.width / 2 - containerRect.width / 2;
  const maxScroll = Math.max(0, container.scrollWidth - container.clientWidth);
  const clamped = Math.max(0, Math.min(target, maxScroll));

  // Ignore tiny deltas (e.g. selected border width) that fight scroll-snap
  if (Math.abs(container.scrollLeft - clamped) < 12) return false;

  container.scrollTo({ left: clamped, behavior: 'smooth' });
  return true;
};

const getCenteredTileIndex = (
  container: HTMLDivElement,
  tiles: (HTMLDivElement | null)[]
): number => {
  const containerRect = container.getBoundingClientRect();
  const centerX = containerRect.left + containerRect.width / 2;
  let bestIndex = -1;
  let bestDist = Infinity;

  tiles.forEach((tile, index) => {
    if (!tile) return;
    const rect = tile.getBoundingClientRect();
    const dist = Math.abs(rect.left + rect.width / 2 - centerX);
    if (dist < bestDist) {
      bestDist = dist;
      bestIndex = index;
    }
  });

  return bestIndex;
};

const GameCarousel: React.FC<GameCarouselProps> = ({
  games,
  currentGameType,
  votes = {},
  onSelectGame,
  onPremiumClick,
  isPremiumRoom = false,
}) => {
  const { t } = useTranslation();
  // Games are already localized by the parent (GameSelection); do not re-translate
  // or branded titles like "{{company}} Quiz" get overwritten.
  const displayGames = games;
  const [visibleColumns, setVisibleColumns] = useState(getVisibleColumns());
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const tileRefs = useRef<(HTMLDivElement | null)[]>([]);
  /** Skip scroll-into-view when selection came from the focused scroll snap */
  const skipScrollIntoViewRef = useRef(false);
  /** Ignore focus-select until this time (ms) — covers programmatic center scroll */
  const ignoreFocusSelectUntilRef = useRef(0);
  const currentGameTypeRef = useRef(currentGameType);
  currentGameTypeRef.current = currentGameType;
  const selectedAccent = getSelectedAccent();
  const totalColumns = displayGames.length;

  useEffect(() => {
    const updateVisibleColumns = () => setVisibleColumns(getVisibleColumns());
    updateVisibleColumns();
    window.addEventListener('resize', updateVisibleColumns);
    return () => window.removeEventListener('resize', updateVisibleColumns);
  }, []);

  const updateArrowVisibility = () => {
    if (!containerRef.current) return;
    const container = containerRef.current;
    const { scrollLeft, scrollWidth, clientWidth } = container;
    setCanScrollLeft(scrollLeft > 0);
    setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
  };

  const trySelectGame = (
    gameId: string,
    options: { fromScroll?: boolean } = {}
  ): boolean => {
    const game = displayGames.find((g) => g.id === gameId);
    if (!game) return false;

    const isPremiumGame = game.premium === true;
    const isMonthlyFree = isPremiumGame && !isPremiumRoom && isMonthlyFreeGame(gameId);
    const isLockedPremium = isPremiumGame && !isPremiumRoom && !isMonthlyFree;

    if (isLockedPremium) {
      // Don't open premium modal while browsing via scroll/arrows
      if (!options.fromScroll) onPremiumClick?.();
      return false;
    }

    if (gameId === currentGameTypeRef.current) return false;
    currentGameTypeRef.current = gameId; // optimistic — avoids duplicate emit on scrollend+debounce
    onSelectGame(gameId);
    return true;
  };

  /** On single-column (snap) layouts, select whichever tile is centered after scroll settles. */
  const selectFocusedTile = () => {
    if (visibleColumns !== 1 || !containerRef.current) return;
    if (Date.now() < ignoreFocusSelectUntilRef.current) return;

    const index = getCenteredTileIndex(containerRef.current, tileRefs.current);
    if (index < 0) return;
    const game = displayGames[index];
    if (!game) return;

    skipScrollIntoViewRef.current = true;
    const didSelect = trySelectGame(game.id, { fromScroll: true });
    if (!didSelect) {
      skipScrollIntoViewRef.current = false;
    }
  };

  useEffect(() => {
    const timeoutId = setTimeout(updateArrowVisibility, 100);
    const container = containerRef.current;
    if (!container) {
      return () => clearTimeout(timeoutId);
    }

    let scrollSettleTimer: ReturnType<typeof setTimeout> | undefined;
    const onScrollSettle = () => {
      if (scrollSettleTimer) {
        clearTimeout(scrollSettleTimer);
        scrollSettleTimer = undefined;
      }
      updateArrowVisibility();
      selectFocusedTile();
    };
    const onScroll = () => {
      updateArrowVisibility();
      // Debounce until snap/smooth scroll finishes (scrollend polyfill)
      if (scrollSettleTimer) clearTimeout(scrollSettleTimer);
      scrollSettleTimer = setTimeout(onScrollSettle, 280);
    };

    container.addEventListener('scroll', onScroll, { passive: true });
    container.addEventListener('scrollend', onScrollSettle);
    window.addEventListener('resize', updateArrowVisibility);
    return () => {
      clearTimeout(timeoutId);
      if (scrollSettleTimer) clearTimeout(scrollSettleTimer);
      container.removeEventListener('scroll', onScroll);
      container.removeEventListener('scrollend', onScrollSettle);
      window.removeEventListener('resize', updateArrowVisibility);
    };
    // selectFocusedTile closes over latest games / visibleColumns
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleColumns, displayGames, isPremiumRoom, onPremiumClick, onSelectGame]);

  useEffect(() => {
    if (!currentGameType || !containerRef.current) return;

    if (skipScrollIntoViewRef.current) {
      skipScrollIntoViewRef.current = false;
      updateArrowVisibility();
      return;
    }

    const index = displayGames.findIndex((game) => game.id === currentGameType);
    const tile = index >= 0 ? tileRefs.current[index] : null;
    if (tile) {
      // rAF so layout (selected border) settles before measuring
      requestAnimationFrame(() => {
        if (!containerRef.current || !tile) return;
        const didScroll = scrollTileIntoView(tile, containerRef.current);
        if (didScroll) {
          // Don't re-pick a different tile while centering this selection
          ignoreFocusSelectUntilRef.current = Date.now() + 450;
        }
        setTimeout(updateArrowVisibility, 350);
      });
    }
  }, [currentGameType, displayGames]);

  const handleTileClick = (_index: number, gameId: string) => {
    trySelectGame(gameId);
  };

  const getScrollStep = (): number => {
    const container = containerRef.current;
    if (!container) return 0;
    const firstTile = tileRefs.current[0];
    const gap =
      parseFloat(
        getComputedStyle(container.querySelector('.game-tiles-grid') || container).columnGap || '0'
      ) || 0;
    if (firstTile) return firstTile.offsetWidth + gap;
    return container.clientWidth / visibleColumns;
  };

  const scrollLeft = () => {
    if (!containerRef.current) return;
    containerRef.current.scrollBy({ left: -getScrollStep(), behavior: 'smooth' });
    setTimeout(updateArrowVisibility, 300);
  };

  const scrollRight = () => {
    if (!containerRef.current) return;
    containerRef.current.scrollBy({ left: getScrollStep(), behavior: 'smooth' });
    setTimeout(updateArrowVisibility, 300);
  };

  return (
    <div className="game-carousel-root">
      <div className="game-carousel-row">
        <button
          type="button"
          onClick={canScrollLeft ? scrollLeft : undefined}
          className="carousel-arrow carousel-arrow-left"
          aria-label={t('gameplay.carousel.scrollLeft')}
          disabled={!canScrollLeft}
        >
          <CarouselChevron direction="left" />
        </button>

        <div className="game-carousel-track">
          <div className="game-tiles-container" ref={containerRef}>
            <div
              className="game-tiles-grid"
              style={{
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                ['--carousel-visible-cols' as any]: visibleColumns,
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                ['--carousel-total-cols' as any]: totalColumns,
                gridTemplateColumns: `repeat(${totalColumns}, minmax(10.75rem, 1fr))`,
                width: `calc(100% * ${totalColumns} / ${visibleColumns} + ${totalColumns - 1} * var(--lobby-carousel-gap, 1rem) - (${visibleColumns} - 1) * var(--lobby-carousel-gap, 1rem) * ${totalColumns} / ${visibleColumns})`,
              }}
            >
              {displayGames.map((game, index) => {
                const isSelected = currentGameType === game.id;
                const voteCount = votes[game.id] || 0;
                const isPremium = game.premium === true;
                const isMonthlyFree = isPremium && !isPremiumRoom && isMonthlyFreeGame(game.id);

                return (
                  <GameCarouselTile
                    key={game.id}
                    game={game}
                    isSelected={isSelected}
                    voteCount={voteCount}
                    isMonthlyFree={isMonthlyFree}
                    isPremium={isPremium}
                    selectedAccent={selectedAccent}
                    onClick={() => handleTileClick(index, game.id)}
                    onPremiumClick={onPremiumClick}
                    tileRef={(el) => {
                      tileRefs.current[index] = el;
                    }}
                  />
                );
              })}
            </div>
          </div>
        </div>

        <button
          type="button"
          onClick={canScrollRight ? scrollRight : undefined}
          className="carousel-arrow carousel-arrow-right"
          aria-label={t('gameplay.carousel.scrollRight')}
          disabled={!canScrollRight}
        >
          <CarouselChevron direction="right" />
        </button>
      </div>
    </div>
  );
};

export default GameCarousel;
